import React, { useState, useEffect, useRef } from 'react';
import api from '../../api';

const ACCENT = '#2B7FFF';
const SURFACE = '#0B1120';
const CARD = '#0F1929';
const BORDER = 'rgba(255,255,255,0.07)';
const OFF_WHITE = '#A0AABB';
const GREEN = '#10b981';
const AMBER = '#f59e0b';
const RED = '#ef4444';

const PLATFORM_ICONS = {
  tiktok: '',
  reels: '',
  shorts: '▶️',
  twitter: '',
  feed: '',
};

const PLATFORM_LABELS = {
  tiktok: 'TikTok',
  reels: 'Instagram Reels',
  shorts: 'YouTube Shorts',
  twitter: 'Twitter/X',
  feed: 'Instagram Feed',
};

const STATUS_LABELS = {
  pending: { label: 'Queued', color: OFF_WHITE },
  generating_image: { label: 'Generating Images...', color: AMBER },
  generating_video: { label: 'Generating Video...', color: AMBER },
  repurposing: { label: 'Repurposing...', color: AMBER },
  uploading: { label: 'Uploading to Buffer...', color: AMBER },
  completed: { label: 'Complete', color: GREEN },
  failed: { label: 'Failed', color: RED },
};

export default function ContentProduction() {
  const [config, setConfig] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [script, setScript] = useState('');
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState(['tiktok', 'reels', 'shorts']);
  const [generating, setGenerating] = useState(false);
  const [activeJobId, setActiveJobId] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  useEffect(() => {
    loadConfig();
    loadTemplates();
    loadJobs();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const loadConfig = async () => {
    try {
      const r = await api.get('/content/config');
      setConfig(r.data);
    } catch (e) {
      console.error(e);
    }
  };

  const loadTemplates = async () => {
    try {
      const r = await api.get('/content/templates');
      setTemplates(r.data.templates || []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadJobs = async () => {
    try {
      const r = await api.get('/content/jobs');
      setJobs(r.data.jobs || []);
    } catch (e) {
      console.error(e);
    }
  };

  const applyTemplate = (template) => {
    setSelectedTemplate(template.id);
    setScript(template.script);
    setCaption(template.caption);
    setHashtags(template.hashtags);
  };

  const togglePlatform = (platform) => {
    setSelectedPlatforms(prev =>
      prev.includes(platform) ? prev.filter(p => p !== platform) : [...prev, platform]
    );
  };

  const startGeneration = async () => {
    if (!script.trim()) {
      setError('Please enter a script or select a template');
      return;
    }
    if (selectedPlatforms.length === 0) {
      setError('Please select at least one platform');
      return;
    }

    setError('');
    setGenerating(true);
    setActiveJob(null);

    try {
      const r = await api.post('/content/generate', {
        script,
        caption,
        hashtags,
        platforms: selectedPlatforms,
      });

      const jobId = r.data.jobId;
      setActiveJobId(jobId);

      // Start polling
      pollRef.current = setInterval(async () => {
        try {
          const statusRes = await api.get(`/content/status/${jobId}`);
          const job = statusRes.data;
          setActiveJob(job);

          if (job.status === 'completed' || job.status === 'failed') {
            clearInterval(pollRef.current);
            setGenerating(false);
            loadJobs(); // Refresh job list
          }
        } catch (e) {
          console.error(e);
        }
      }, 4000);
    } catch (e) {
      setError(e.response?.data?.error || 'Generation failed');
      setGenerating(false);
    }
  };

  const getStatusInfo = (status) => STATUS_LABELS[status] || { label: status, color: OFF_WHITE };

  return (
    <div style={{ color: '#fff', fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <div style={{ marginBottom: '28px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 900, margin: 0 }}>AI Content Production</h2>
        <p style={{ color: OFF_WHITE, fontSize: '13px', marginTop: '4px' }}>
          Generate professional videos + images with DALL-E & Runway, then push to Buffer
        </p>
      </div>

      {/* Config Status */}
      {config && (
        <div style={{ display: 'flex', gap: '10px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <ServiceBadge label="DALL-E 3" active={config.openaiConfigured} />
          <ServiceBadge label="Runway Gen-4" active={config.runwayConfigured} />
          <ServiceBadge label="Buffer" active={config.bufferConfigured} />
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* LEFT: Script Builder */}
        <div>
          {/* Templates */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: OFF_WHITE, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>
              Quick Templates
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {templates.map(t => (
                <button
                  key={t.id}
                  onClick={() => applyTemplate(t)}
                  style={{
                    padding: '12px 14px',
                    background: selectedTemplate === t.id ? `${ACCENT}22` : CARD,
                    border: `1px solid ${selectedTemplate === t.id ? ACCENT : BORDER}`,
                    borderRadius: '8px',
                    color: selectedTemplate === t.id ? '#fff' : '#bbb',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.15s',
                  }}
                >
                  {t.title}
                  <span style={{ fontSize: '11px', color: '#555', marginLeft: '8px', fontWeight: 400 }}>{t.category}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Script Input */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: OFF_WHITE, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Video Script / Prompt *
            </label>
            <textarea
              value={script}
              onChange={e => setScript(e.target.value)}
              placeholder="Describe the video you want to create..."
              rows={4}
              style={{
                width: '100%', boxSizing: 'border-box',
                background: CARD, border: `1px solid ${BORDER}`,
                borderRadius: '8px', color: '#fff', fontSize: '13px',
                padding: '12px', resize: 'vertical',
                fontFamily: 'Inter, sans-serif',
              }}
            />
          </div>

          {/* Caption */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: OFF_WHITE, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Caption
            </label>
            <textarea
              value={caption}
              onChange={e => setCaption(e.target.value)}
              placeholder="Caption for your post..."
              rows={3}
              style={{
                width: '100%', boxSizing: 'border-box',
                background: CARD, border: `1px solid ${BORDER}`,
                borderRadius: '8px', color: '#fff', fontSize: '13px',
                padding: '12px', resize: 'vertical',
                fontFamily: 'Inter, sans-serif',
              }}
            />
          </div>

          {/* Hashtags */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: OFF_WHITE, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Hashtags
            </label>
            <input
              type="text"
              value={hashtags}
              onChange={e => setHashtags(e.target.value)}
              placeholder="#barbershop #freshcut #barberlife"
              style={{
                width: '100%', boxSizing: 'border-box',
                background: CARD, border: `1px solid ${BORDER}`,
                borderRadius: '8px', color: '#fff', fontSize: '13px',
                padding: '12px', fontFamily: 'Inter, sans-serif',
              }}
            />
          </div>

          {/* Platform Selection */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: OFF_WHITE, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px' }}>
              Target Platforms
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {Object.keys(PLATFORM_LABELS).map(platform => (
                <button
                  key={platform}
                  onClick={() => togglePlatform(platform)}
                  style={{
                    padding: '8px 14px',
                    background: selectedPlatforms.includes(platform) ? `${ACCENT}33` : CARD,
                    border: `1px solid ${selectedPlatforms.includes(platform) ? ACCENT : BORDER}`,
                    borderRadius: '20px', color: '#fff', fontSize: '12px',
                    fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  {PLATFORM_ICONS[platform]} {PLATFORM_LABELS[platform]}
                </button>
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={{ background: `${RED}22`, border: `1px solid ${RED}`, borderRadius: '8px', padding: '10px 14px', color: RED, fontSize: '13px', marginBottom: '16px' }}>
              {error}
            </div>
          )}

          {/* Generate Button */}
          <button
            onClick={startGeneration}
            disabled={generating}
            style={{
              width: '100%', padding: '14px',
              background: generating ? '#333' : ACCENT,
              border: 'none', borderRadius: '10px', color: '#fff',
              fontSize: '14px', fontWeight: 800, cursor: generating ? 'not-allowed' : 'pointer',
              letterSpacing: '0.02em',
            }}
          >
            {generating ? 'Generating...' : 'Generate Content'}
          </button>
        </div>

        {/* RIGHT: Status + Preview */}
        <div>
          {/* Active Job Status */}
          {activeJob && (
            <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: OFF_WHITE, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '14px' }}>
                Generation Status
              </div>

              <StatusStep label="DALL-E Images" done={['generating_video', 'repurposing', 'uploading', 'completed'].includes(activeJob.status)} active={activeJob.status === 'generating_image'} />
              <StatusStep label="Runway Video" done={['repurposing', 'uploading', 'completed'].includes(activeJob.status)} active={activeJob.status === 'generating_video'} skipped={!config?.runwayConfigured} />
              <StatusStep label="Platform Repurposing" done={['uploading', 'completed'].includes(activeJob.status)} active={activeJob.status === 'repurposing'} />
              <StatusStep label="Buffer Scheduling" done={activeJob.status === 'completed'} active={activeJob.status === 'uploading'} skipped={!config?.bufferConfigured} />

              {activeJob.status === 'failed' && (
                <div style={{ marginTop: '12px', padding: '10px', background: `${RED}22`, borderRadius: '8px', color: RED, fontSize: '12px' }}>
                  {activeJob.error_message || 'Generation failed'}
                </div>
              )}

              {activeJob.seed_image_url && (
                <div style={{ marginTop: '16px' }}>
                  <div style={{ fontSize: '11px', color: OFF_WHITE, marginBottom: '6px', fontWeight: 700 }}>Seed Image</div>
                  <img
                    src={activeJob.seed_image_url}
                    alt="Seed"
                    style={{ width: '100%', borderRadius: '8px', maxHeight: '200px', objectFit: 'cover' }}
                  />
                </div>
              )}

              {activeJob.output_url && (
                <div style={{ marginTop: '16px' }}>
                  <div style={{ fontSize: '11px', color: OFF_WHITE, marginBottom: '6px', fontWeight: 700 }}>Generated Video</div>
                  <video
                    src={activeJob.output_url}
                    controls
                    style={{ width: '100%', borderRadius: '8px', background: '#000' }}
                  />
                </div>
              )}

              {/* Assets */}
              {activeJob.assets && activeJob.assets.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <div style={{ fontSize: '11px', color: OFF_WHITE, marginBottom: '8px', fontWeight: 700 }}>Platform Assets</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {activeJob.assets.map(asset => (
                      <div key={asset.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', padding: '6px 10px', background: '#0a0f1a', borderRadius: '6px' }}>
                        <span>{PLATFORM_ICONS[asset.platform]} {PLATFORM_LABELS[asset.platform]} ({asset.type})</span>
                        <span style={{ color: asset.status === 'scheduled' ? GREEN : asset.status === 'ready' ? ACCENT : OFF_WHITE }}>
                          {asset.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Recent Jobs */}
          <div>
            <div style={{ fontSize: '12px', fontWeight: 700, color: OFF_WHITE, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '12px' }}>
              Recent Jobs
            </div>
            {jobs.length === 0 ? (
              <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '10px', padding: '28px', textAlign: 'center', color: '#444', fontSize: '13px' }}>
                No jobs yet. Generate your first piece of content!
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {jobs.slice(0, 8).map(job => {
                  const statusInfo = getStatusInfo(job.status);
                  return (
                    <div key={job.id} style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '10px', padding: '12px 14px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1, marginRight: '10px' }}>
                          <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '2px' }}>
                            {job.script.slice(0, 60)}{job.script.length > 60 ? '...' : ''}
                          </div>
                          <div style={{ fontSize: '11px', color: '#555' }}>
                            {new Date(job.created_at).toLocaleDateString()} · {job.video_asset_count || 0} videos · {job.image_asset_count || 0} images
                          </div>
                        </div>
                        <span style={{ fontSize: '11px', fontWeight: 700, color: statusInfo.color, whiteSpace: 'nowrap' }}>
                          {statusInfo.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function ServiceBadge({ label, active }) {
  return (
    <div style={{
      padding: '5px 12px',
      background: active ? `${GREEN}22` : 'rgba(255,255,255,0.04)',
      border: `1px solid ${active ? GREEN : BORDER}`,
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 700,
      color: active ? GREEN : '#555',
    }}>
      {active ? '✓' : '✗'} {label}
    </div>
  );
}

function StatusStep({ label, done, active, skipped }) {
  let icon = '○';
  let color = '#444';

  if (skipped) { icon = '—'; color = '#333'; }
  else if (done) { icon = '✓'; color = GREEN; }
  else if (active) { icon = '⏳'; color = AMBER; }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0' }}>
      <span style={{ color, fontWeight: 700, width: '20px', textAlign: 'center' }}>{icon}</span>
      <span style={{ fontSize: '13px', color: done ? '#ccc' : active ? '#fff' : '#555' }}>
        {label}
        {skipped && <span style={{ fontSize: '11px', color: '#333', marginLeft: '6px' }}>(not configured)</span>}
      </span>
    </div>
  );
}
